import { Icon } from "@/components/icons/Icon";
import { classNames } from "@/utils/classnames";

export interface SsoLogoProps {
  id: string;
  name: string;
  type: string;
  className?: string;
}

export function SsoLogo({ id, name, type, className }: SsoLogoProps) {
  if (id === "facebook") {
    return (
      <svg
        className={classNames("h-5 w-5", className)}
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (id === "twitter") {
    return (
      <svg
        className={classNames("h-5 w-5", className)}
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
      </svg>
    );
  }

  if (id === "github") {
    return (
      <svg
        className={classNames("h-5 w-5", className)}
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (id === "google") {
    return (
      <div className={classNames("h-5 w-5", className)}>
        <img src="/static/google_logo.png" alt="Google Logo" />
      </div>
    );
  }

  if (id === "azure-ad" || name === "azure-ad") {
    return (
      <div className={classNames("h-5 w-5", className)}>
        <img src="/static/azure_ad_logo.svg" alt="Azure AD Logo" />
      </div>
    );
  }

  if (id === "okta" || name === "Okta" || name === "okta") {
    return (
      <div className={classNames("h-5 w-5", className)}>
        <img src="/static/okta_logo.png" alt="Okta Logo" />
      </div>
    );
  }

  if (id === "onelogin" || name === "OneLogin" || name === "onelogin") {
    return (
      <div className={classNames("h-5 w-5", className)}>
        <img src="/static/onelogin_logo.png" alt="Onelogin Logo" />
      </div>
    );
  }

  if (id === "keycloak" || name === "Keycloak") {
    return (
      <div className={classNames("h-5 w-5", className)}>
        <img src="/static/keycloak_logo.png" alt="Keycloak Logo" />
      </div>
    );
  }

  if (type === "saml") {
    return (
      <div className={classNames("flex h-5 w-5 items-center justify-center", className)}>
        <Icon name="user" size="lg" />
      </div>
    );
  }

  return null;
}

export function getSsoButtonLabel({ id, name, type }: Omit<SsoLogoProps, "className">): string {
  if (id === "github") return "GitHub";
  if (id === "google") return "Google";
  if (id === "azure-ad" || name === "azure-ad") {
    return `Azure Active Directory${type === "saml" ? " (SSO)" : ""}`;
  }
  if (id === "okta" || name === "Okta" || name === "okta") {
    return `Okta ${type === "saml" ? "(SSO)" : "(OIDC)"}`;
  }
  if (id === "onelogin" || name === "OneLogin" || name === "onelogin") {
    return `OneLogin ${type === "saml" ? "(SSO)" : "(OIDC)"}`;
  }
  if (id === "keycloak" || name === "Keycloak") return "Continue with Keycloak";
  if (type === "saml") return "Continue with SSO";
  return name;
}
